/**
 * The product-line card, resolved to the car we already know about.
 *
 * A card that says "Wind deflectors · from £39" is the right thing to show
 * somebody we know nothing about. It is the wrong thing to show somebody whose
 * reg we resolved two scrolls further up the same page — for them the card can
 * say the exact price, show the set that actually fits, and lead to their car's
 * landing page rather than to the generic one. Every other surface in this store
 * already makes that promotion once a car is known: the router row, the PDP
 * cross-sell band, the cart drawer's card. The home page's two big cards were
 * the last place still quoting a range at a resolved customer.
 *
 * ── What it is not ──────────────────────────────────────────────────────────
 * It is NOT a second resolution engine. Every number here is the one
 * `paintRouter` would print for the same car, by the same rules and in the same
 * words — cheapest in stock, "from" only where there is more than one, the
 * version limit where a category covers some versions and not all. Where the two
 * disagree, this file is wrong. The reason it is a separate file at all is
 * placement: `airflux-fitment.js` paints into a widget it owns, and these cards
 * are ordinary page furniture that no widget contains.
 *
 * ── Where the numbers come from ─────────────────────────────────────────────
 * `fitment-index.json`, through the sessionStorage key `airflux-fitment.js`
 * writes — so on the home page, where the hero has already loaded it, this costs
 * no request at all. One `/products/<handle>.js` per resolved category buys the
 * photograph, and those are the same URLs the hero has already fetched for the
 * same car, so they come out of the browser's cache.
 *
 * With no car stored, or with JavaScript off, nothing here runs and the card is
 * exactly the card the section rendered.
 */
(() => {
  'use strict';
  if (window.AirfluxCards) return;

  const CAR_KEY = 'airflux:car';
  const money = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });

  /** `data-af-card` speaks the merchant's language; the index speaks in letters. */
  const TYPE = { deflectors: 'd', wipers: 'w' };
  const NOUN = { d: 'Wind deflectors', w: 'Wiper blades' };

  /* Through `window.AirfluxStorage`, never `localStorage` directly: the car is
     kept for the visit unconditionally and mirrored across visits only on
     consent. See `snippets/airflux-storage.liquid`. */
  const readCar = () => window.AirfluxStorage?.readJSON(CAR_KEY) || null;

  /** Fires a DOM event and mirrors it to dataLayer when one exists (§30).
      The push is gated on analytics consent — see the long note on `track` in
      `airflux-fitment.js` for why the DOM half is not. */
  const track = (name, detail = {}) => {
    document.dispatchEvent(new CustomEvent(`airflux:${name}`, { detail, bubbles: true }));
    if (window.AirfluxStorage?.may('analytics') && Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: `airflux_${name}`, ...detail });
    }
  };

  // ── the index ──────────────────────────────────────────────────────────────

  let indexPromise = null;

  /**
   * 🔴 The cache key is `airflux-fitment.js`'s, including the asset URL.
   *
   * Sharing it is the whole reason this is cheap: on the home page the hero has
   * already put a parsed copy there, and on any page that has not, one fetch
   * serves the finder that loads next. The URL is part of the key because
   * Shopify's `?v=` changes on every index sync, and a fixed key served an open
   * tab a stale copy for the rest of the session — a car minted after that copy
   * was taken then resolves to nothing.
   */
  function loadIndex(url) {
    if (indexPromise) return indexPromise;
    if (!url) return Promise.resolve(null);
    const cacheKey = `airflux:index:${url}`;

    indexPromise = (async () => {
      try {
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) return JSON.parse(cached);
      } catch {
        // A corrupt copy is not worth a failed fetch.
      }
      try {
        const res = await fetch(url, { headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify(data));
        } catch {
          // Quota. The copy in memory is good for this page.
        }
        return data;
      } catch {
        // No retry ladder, unlike the finder's: this is a promotion on top of a
        // card that already says something true. Silence is the right failure.
        return null;
      }
    })();

    return indexPromise;
  }

  const products = new Map();

  /** The live product, for its photograph. One request per handle per page. */
  function product(handle) {
    if (!products.has(handle)) {
      products.set(
        handle,
        fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } })
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null)
      );
    }
    return products.get(handle);
  }

  // ── resolution, by the router's rules ──────────────────────────────────────

  /** `MAKE MODEL BODY` — `label()` in `airflux-fitment.js`, and it must stay it. */
  const label = (index, v) => `${index.makes[v.m]} ${v.n}${v.b ? ` ${v.b}` : ''}`;

  /**
   * The car as the customer described it. The year rides along only where the
   * plate lookup actually gave us one — the manual cascade never does, and
   * guessing it is the sort of invented fact this store does not print.
   */
  const carLabel = (index, v, car) => `${label(index, v)}${car?.handle === v.h && car.year ? ` ${car.year}` : ''}`;

  /**
   * "E90 · 4-door Saloon only" — where a category covers some versions, not all.
   *
   * `versionLimit()` in `airflux-fitment.js`, to the word. It is the difference
   * between an honest row and a promise we cannot keep: on nine cars in the
   * catalogue the category we stock is a fact about the VERSION, not about the
   * car, and without this an E91 owner reads "Wind deflectors £34.90" off a
   * listing that only ever fitted the E90 saloon.
   */
  function versionLimit(index, v, t) {
    if (!v.vo?.length) return null;
    const covers = v.vo.filter((o) => (o.p ?? []).some((i) => index.products[i]?.t === t));
    // Everything, or nothing we can describe — say nothing either way. A
    // qualifier naming every option is noise; one naming none would be a claim
    // we cannot support.
    if (!covers.length || covers.length === v.vo.length) return null;
    return `${covers.map((o) => o.l).join(', ')} only`;
  }

  /**
   * What we make for this car in one category, or null where we make nothing.
   *
   * @returns {null|{price: string, limit: ?string, qualifier: ?string, handle: string}}
   */
  function resolve(index, v, t, version) {
    // A version the customer has already told us narrows the answer rather than
    // qualifying it — an E91 owner should not be offered the E90's kit with a
    // footnote, they should not be offered it.
    const chosen = (v.vo ?? []).find((o) => o.l === version) ?? null;

    let ids = t === 'd' ? v.d : v.w;
    if (chosen) ids = (ids ?? []).filter((i) => (chosen.p ?? []).includes(i));
    if (!ids?.length) return null;

    const pool = ids.map((i) => index.products[i]).filter(Boolean);
    if (!pool.length) return null;

    // In stock first — a "from" price nobody can pay is worse than a slightly
    // higher one they can. This is the whole pool; the quoted price is narrowed
    // to the listings that survive the collapse further down.
    const sellable = pool.filter((p) => p.a);
    const priced = sellable.length ? sellable : pool;
    const cheapest = [...priced].sort((a, b) => a.p - b.p)[0];
    if (!cheapest) return null;

    /**
     * Naming the channel is only safe where there is ONE thing to buy.
     *
     * `counterparts()` collapses the category to genuinely different purchases —
     * an in-channel set and an out-channel set are two, two listings of the same
     * kit are one. Where it returns more than one, "In-channel" would name a
     * single option off a card whose price covers all of them.
     */
    /**
     * `counterparts()` defaults to newest-upload, then in stock, then cheapest,
     * then handle. `render()` slips one more clause in ahead of the handle:
     * where two listings tie on all three, the one whose title NAMES a chassis
     * code this car is sold under wins — the Berlingo case, where "BERLINGO MK3"
     * and "BERLINGO, Fits PARTNER, RIFTER…" are the same £34.90 kit and the
     * first is the clearer page to land on. Passed in rather than left to the default, because a winner decided by
     * luck is a winner that changes the day the catalogue does. It alters no
     * group's SIZE, only which member represents it, so `qualifier` below is
     * unaffected.
     */
    const rowCodes = String(v.g || '').split(/[\s,]+/).filter(Boolean);
    const namesRow = (c) => String(c.g || '').split(/[\s,]+/).filter(Boolean).some((g) => rowCodes.includes(g));
    const preferred = (a, b) => {
      // Newest upload day first, ahead of stock and price — §3.2a as revised
      // 2026-09-05. Stated here as well as in `counterparts()`'s own default so
      // this card can never disagree with the landing page it links to; the
      // clause order is `preferred()` in airflux-fitment.js, verbatim.
      if (String(a.u ?? '') !== String(b.u ?? '')) return String(a.u ?? '') > String(b.u ?? '');
      if (!!a.a !== !!b.a) return !!a.a;
      if (a.p !== b.p) return a.p < b.p;
      if (namesRow(a) !== namesRow(b)) return namesRow(a);
      return String(a.h).localeCompare(String(b.h)) < 0;
    };

    const distinct = window.AirfluxXsell?.counterparts(index, { ...v, d: ids, w: ids }, t, preferred) ?? [];
    const qualifier = distinct.length === 1 ? window.AirfluxXsell.qualifierText(distinct[0]) || null : null;

    /**
     * 🔴 The photograph is the product the LANDING PAGE OPENS ON.
     *
     * It used to be `pool[0]` — the first listing the index happens to name for
     * this car — justified by a comment claiming the landing page "resolves to
     * the first". It does not, and never did. `render()` in `airflux-fitment.js`
     * collapses duplicates and then sorts what survives by price, channel, pack
     * and handle, opening on `visible[0]`. Index order is not part of it.
     *
     * So the two disagreed exactly where a car carries two listings of the same
     * kit. Found on the Škoda Karoq: two in-channel 4-piece sets, both £34.90,
     * both in stock, differing only in that one handle ends `-2024-models` and
     * the other `-2024-2025-models`. The first carries nine photographs and the
     * second none — and the tie-break sends the landing page to the second. The
     * card showed a photograph of a kit the customer never arrived at, which is
     * precisely the "tap one picture, arrive at another" the old comment set out
     * to prevent.
     *
     * `counterparts()` already applies §3.2a's collapse and its winner (newest
     * upload, then in stock, then cheapest, then handle). What it does not do is
     * the final ORDER, so that is re-stated here from `render()`, minus the
     * category clause — a card
     * is one category, so it can never discriminate. In-channel wins a price tie
     * for the reason given there: it is 390 of the 478 deflector listings, and a
     * stated rule beats Map insertion order.
     *
     * Still the index's own price and stock, not a live read. The landing page
     * fetches every candidate before it decides; a card that did the same would
     * turn one request into one per listing on a surface that is not the sale.
     * Same rule, cheaper evidence — so the two can still part company if stock
     * moved since the last index build, and a stale thumbnail is the whole cost.
     */
    const CHANNEL_RANK = { 'in-channel': 0, 'out-channel': 1 };
    const opens = [...distinct].sort(
      (a, b) =>
        a.p - b.p ||
        (CHANNEL_RANK[a.c] ?? 2) - (CHANNEL_RANK[b.c] ?? 2) ||
        (a.k ?? 0) - (b.k ?? 0) ||
        String(a.h).localeCompare(String(b.h))
    )[0];
    // `cheapest`, not `pool[0]`, if the cross-sell module never loaded: it is
    // the same answer whenever the car has one thing to buy, which is most of
    // them, and never an arbitrary one.
    const shown = opens ?? cheapest;

    /**
     * 🔴 The price is quoted off what the collapse LEFT, not off `pool`.
     *
     * Until 2026-09-05 the two were the same thing — the collapse kept the
     * cheapest, so the cheapest listing was always the one on offer. The rule
     * now keeps the newest upload, so `pool`'s cheapest can be a listing the
     * store never shows. Caught in the browser on the Dacia Bigster: this card
     * read "from £35.90" while the page it links to charged £44.90, because the
     * £35.90 listing is the superseded one. `paintRouter` in airflux-fitment.js
     * quotes the same way, through `quotable()` there.
     *
     * `priced` is the fallback for a page where the cross-sell module never
     * loaded, and it is never empty — `pool` was checked above — so the sort
     * below always has something to take.
     */
    const reach = distinct.length ? distinct : priced;
    const inStock = reach.filter((p) => p.a);
    const quotable = inStock.length ? inStock : reach;
    const from = [...quotable].sort((a, b) => a.p - b.p)[0];

    return {
      price: `${quotable.length > 1 ? 'from ' : ''}${money.format(from.p / 100)}`,
      limit: chosen ? null : versionLimit(index, v, t),
      qualifier,
      handle: shown.h,
    };
  }

  // ── painting ───────────────────────────────────────────────────────────────

  /**
   * What the section rendered, kept so the card can go back to it.
   *
   * A car can be cleared, or changed to one we cover differently, at any time and
   * from anywhere on the page — so "unresolved" has to be a state this file can
   * return to, not just the state it happened to start in.
   */
  function original(card) {
    if (!card._afOriginal) {
      const link = card.querySelector('[data-af-card-link]');
      card._afOriginal = {
        href: link?.getAttribute('href') ?? null,
        price: card.querySelector('[data-af-card-price]')?.textContent ?? '',
        type: card.querySelector('[data-af-card-type]')?.textContent ?? '',
      };
    }
    return card._afOriginal;
  }

  const slots = (card) => ({
    link: card.querySelector('[data-af-card-link]'),
    thumb: card.querySelector('[data-af-card-thumb]'),
    for: card.querySelector('[data-af-card-for]'),
    metaRow: card.querySelector('[data-af-card-meta-row]'),
    price: card.querySelector('[data-af-card-price]'),
    divider: card.querySelector('[data-af-card-divider]'),
    type: card.querySelector('[data-af-card-type]'),
    go: card.querySelector('[data-af-card-go]'),
    demand: card.querySelector('[data-af-card-demand]'),
    demandBtn: card.querySelector('[data-af-card-demand-btn]'),
    demandSlot: card.querySelector('[data-af-card-demand-slot]'),
  });

  /** Back to the card the section rendered, whatever state it was left in. */
  function reset(card) {
    const o = original(card);
    const s = slots(card);
    card.removeAttribute('data-af-card-state');
    if (s.link && o.href) s.link.setAttribute('href', o.href);
    if (s.thumb) {
      s.thumb.replaceChildren();
      s.thumb.hidden = true;
    }
    if (s.for) {
      s.for.textContent = '';
      s.for.hidden = true;
    }
    if (s.metaRow) s.metaRow.hidden = false;
    if (s.price) s.price.textContent = o.price;
    if (s.divider) s.divider.hidden = false;
    if (s.type) s.type.textContent = o.type;
    if (s.go) s.go.hidden = false;
    if (s.demand) s.demand.hidden = true;
    if (s.demandSlot) s.demandSlot.replaceChildren();
  }

  /**
   * The card for a car we cover — price, the set's own photograph, and a link
   * that arrives at the landing page with the car already resolved.
   */
  function paintResolved(card, answer, named, url) {
    const s = slots(card);
    original(card);
    card.setAttribute('data-af-card-state', 'resolved');

    if (s.link && url) s.link.setAttribute('href', url);
    if (s.for) {
      s.for.textContent = `for your ${named}`;
      s.for.hidden = false;
    }
    if (s.metaRow) s.metaRow.hidden = false;
    if (s.price) s.price.textContent = answer.price;

    // The version limit outranks the channel: "E90 · 4-door Saloon only" is a
    // condition on whether this fits at all, where "In-channel" only says which
    // of two shapes it is. Where there is neither, the merchant's own line
    // stands — a resolved card should never have less to say than it did.
    const qualifier = answer.limit || answer.qualifier || original(card).type;
    if (s.type) s.type.textContent = qualifier;
    if (s.divider) s.divider.hidden = !qualifier;
    if (s.go) s.go.hidden = false;
    if (s.demand) s.demand.hidden = true;
  }

  /**
   * The card for a category we do not make for this car.
   *
   * Kept in place rather than removed: 83% of the cars in this catalogue carry
   * one line and not the other, so this is the ordinary state of one of the two
   * cards, and a card that quietly vanishes collects nothing. The demand signal
   * it raises is the most valuable thing the visit can produce.
   */
  function paintDemand(card, named, noun, v) {
    const s = slots(card);
    original(card);
    card.setAttribute('data-af-card-state', 'demand');
    // Stashed rather than read back off the page when the button is pressed: the
    // sentence on the card is a sentence, and picking the car back out of it
    // with a regular expression is one rewording away from putting "None for
    // your" on a customer record.
    card._afNamed = named;
    // 🔴 …and the catalogue HANDLE with it, because a demand sign-up from this
    // card was being tagged `car:lexus-ux` — slugged from the label, because
    // nothing here ever passed a key — while every other surface tags
    // `car:lexus-ux-2019`, the vehicle handle. Same car, two tags, and the
    // panel's row for it links to the second: anybody who asked from a card was
    // invisible on the very list their request was meant to feed. This is the
    // split-key failure `signupTags` documents, reached by the one path that
    // never supplied a key.
    card._afKey = v?.h || '';
    // The dated name for the record. The card says "Lexus UX" because the page
    // around it makes the year obvious; a customer note has no page around it.
    card._afDated = `${named}${v?.f && v?.t ? ` ${v.f}–${v.t}` : ''}`;
    // 🔴 …and the catalogue's verdict on the car, stashed for the same reason
    // as the key. `/resolve` is gated on analytics consent and the sign-up is
    // not, so on a refused cookie banner this form is the only thing that ever
    // tells the Worker what we stock for this car — and without it the panel
    // opened the row as "we stock nothing", which is wrong wherever we stock
    // the OTHER line for that car.
    //
    // ⚠️ Computed from the CATALOGUE, not from this card's own emptiness. Both
    // cards can be in this state at once (a car we make neither line for), and
    // a card can be empty for a car we do stock — a version the customer named
    // narrows the products away, see `resolve`. Coverage is a fact about the
    // car, and it has to say what `airflux-fitment.js` reports for the same
    // car, or the two disagree on the row they share.
    const cd = v && v.d && v.d.length ? 1 : 0;
    const cw = v && v.w && v.w.length ? 1 : 0;
    card._afCoverage = cd && cw ? 'both' : cd || cw ? 'one' : 'none';
    card._afStocks = cd && cw ? '' : cd ? 'deflectors' : cw ? 'wipers' : '';

    // 🔴 The href goes, not just the stretched overlay. There is no page to send
    // anybody to — the landing page would answer the question this card is
    // already answering — and a link that looks live and leads nowhere useful is
    // worse than plain text. Without an `href` the anchor stops being a link and
    // stops being focusable, which is exactly right.
    s.link?.removeAttribute('href');

    if (s.thumb) {
      s.thumb.replaceChildren();
      s.thumb.hidden = true;
    }
    if (s.for) {
      s.for.textContent = `None for your ${named} yet`;
      s.for.hidden = false;
    }
    if (s.metaRow) s.metaRow.hidden = true;
    if (s.go) s.go.hidden = true;
    if (s.demand) s.demand.hidden = false;
    if (s.demandBtn) {
      s.demandBtn.textContent = 'Tell us you want them';
      s.demandBtn.hidden = false;
    }
    if (s.demandSlot) s.demandSlot.replaceChildren();

    // `v.h`, not `v` — this is a tracking payload, and the handle is what every
    // other `coverage_gap_hit` in the codebase reports.
    track('coverage_gap_hit', { vehicle: v?.h, scope: noun, via: 'card_demand' });
  }

  /**
   * The photograph, once it arrives.
   *
   * Painted separately from the price for a reason: the price is in the index
   * and is on the card in the same frame, while the picture is a round trip. A
   * card that waited for the image would hold back the number that decides the
   * sale in order to arrive prettier.
   */
  async function paintThumb(card, handle, stamp) {
    const p = await product(handle);
    if (card._afToken !== stamp || !p?.featured_image) return;
    const thumb = card.querySelector('[data-af-card-thumb]');
    if (!thumb) return;
    const img = document.createElement('img');
    // From `/products/<handle>.js`, which is same-origin JSON we asked for by
    // handle — but it goes through the same check the rest of the theme applies
    // to anything that has been out of the page and back.
    const src = String(p.featured_image);
    if (!src.startsWith('//') && !src.startsWith('https://')) return;
    img.src = `${src}&width=300`;
    img.alt = '';
    img.width = 150;
    img.height = 150;
    img.loading = 'lazy';
    thumb.replaceChildren(img);
    thumb.hidden = false;
  }

  // ── the pass ───────────────────────────────────────────────────────────────

  let token = 0;

  /**
   * 🔴 `?car=` outranks storage, but only on arrival.
   *
   * The first half is `applyCarFromUrl`'s rule and holds for the same reason: a
   * link somebody followed is a more deliberate statement than something this
   * browser remembered, and it is what makes a resolved page shareable.
   *
   * The second half is this file's own, and it is not optional. This page's
   * finder is not `layout="hero"`, so it never rewrites the address bar — a
   * customer who lands on `?car=ford-kuga-2020` and then corrects themselves in
   * the panel would have the correction repainted straight back to the Kuga on
   * the very event that announced it.
   */
  let honourUrl = true;

  /** What the page is about, URL first on arrival, storage after. */
  function subject() {
    const car = readCar();
    if (!honourUrl) return { handle: car?.handle ?? null, version: car?.version ?? null, car };
    const params = new URLSearchParams(window.location.search);
    const handle = params.get('car');
    if (!handle) return { handle: car?.handle ?? null, version: car?.version ?? null, car };
    return {
      handle,
      // A version claimed in the URL is checked against the car's own options
      // before it is used — see the note in `applyCarFromUrl`. Here it only
      // reaches a price and a label, but a label is what the customer checks the
      // answer against, and "Audi A3 · Banana" is not a check they can make.
      version: params.get('v') ?? (car?.handle === handle ? car.version : null) ?? null,
      car,
    };
  }

  /**
   * So the page around the cards can name the car they just answered about.
   *
   * 🔴 The last announcement is KEPT, and listeners must read it as well as
   * subscribe. Deferred scripts run one after another and microtasks drain
   * between them, so on any visit where the index is already in sessionStorage —
   * which is most of them — this whole pass finishes during the gap between
   * `airflux-cards.js` and the next script tag. A listener registered in that
   * next script is registered one tick too late and never hears the only event
   * that matters. Reordering the tags would paper over it until somebody moved
   * them back; a replayable last value cannot be got wrong.
   */
  let last = null;
  const announce = (detail) => {
    last = detail;
    document.dispatchEvent(new CustomEvent('airflux:cards-painted', { detail, bubbles: true }));
  };

  async function repaint() {
    const cards = document.querySelectorAll('[data-af-card]');
    if (!cards.length) return;

    // Every paint carries a number, and a late arrival checks it before it
    // touches anything. Changing the car twice quickly is otherwise enough to
    // have the first car's photograph land on the second car's card.
    const mine = ++token;
    for (const card of cards) card._afToken = mine;

    const { handle, version: claimed, car } = subject();
    if (!handle) {
      for (const card of cards) reset(card);
      announce({ car: null });
      return;
    }

    const index = await loadIndex(cards[0].dataset.indexUrl);
    if (!index || token !== mine) return;

    const v = index.vehicles.find((x) => x.h === handle);
    if (!v) {
      // A car this index has never heard of — a handle renamed between the last
      // sync and this visit, or hand-typed. The generic card is the honest
      // answer, not a gap: we are not saying we don't make it, we don't know.
      for (const card of cards) reset(card);
      announce({ car: null });
      return;
    }

    const version = claimed && (v.vo ?? []).some((o) => o.l === claimed) ? claimed : null;
    const named = `${carLabel(index, v, car)}${version ? ` · ${version}` : ''}`;
    let resolved = 0;
    const gaps = [];

    for (const card of cards) {
      const t = TYPE[card.dataset.afCard];
      if (!t) continue;

      const answer = resolve(index, v, t, version);
      if (!answer) {
        // The whole record, not `v.h`. The fourth argument used to be the handle
        // alone; it now carries what the demand form needs too — the years to
        // name the request by. The one place inside that read it takes `.h`.
        paintDemand(card, named, card.dataset.afCard, v);
        gaps.push(card.dataset.afCard);
        continue;
      }

      const target = card.dataset.afCardUrl;
      let href = null;
      if (target) {
        const u = new URL(target, window.location.origin);
        u.searchParams.set('car', v.h);
        if (version) u.searchParams.set('v', version);
        href = u.pathname + u.search;
      }

      paintResolved(card, answer, named, href);
      paintThumb(card, answer.handle, mine);
      resolved++;
    }

    if (resolved) track('price_revealed', { vehicle: v.h, source: 'cards' });

    announce({
      car: {
        handle: v.h,
        named,
        years: `${v.f}–${v.t}`,
        generation: v.g ?? null,
        version,
      },
      resolved,
      gaps,
    });
  }

  /**
   * The demand form is mounted on the press, never on the paint.
   *
   * Two reasons, and the second is the load-bearing one: a card in this state
   * should cost nothing until somebody uses it, and `window.AirfluxCapture` is
   * published by `airflux-fitment.js`, whose script tag this file's has no
   * guaranteed order against. By the time anybody has read a card and pressed a
   * button, every deferred script on the page has long since run.
   */
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-af-card-demand-btn]');
    if (!btn) return;
    const card = btn.closest('[data-af-card]');
    const slot = card?.querySelector('[data-af-card-demand-slot]');
    if (!slot || slot.childElementCount) return;

    // 🔴 The category rides on the slot rather than being glued into the car
    // name — same change the router's demand row took, for the same reason.
    // Pressing THIS button is a shopper naming a product line outright, so it
    // becomes a `want:` tag the client can filter a campaign by; buried in the
    // label it was readable and nothing more.
    //
    // Read off `data-af-card`, which is already 'deflectors' or 'wipers' — the
    // vocabulary the Worker expects. NOT `TYPE[...]`, which maps those to the
    // index's one-letter keys and would send 'd'.
    slot.dataset.want = card.dataset.afCard === 'wipers' ? 'wipers' : 'deflectors';
    // The key is what makes this request land on the panel's row for the car
    // rather than under a tag of its own — see `_afKey`.
    slot.dataset.carKey = card._afKey || '';
    // Read off the slot by `mountCaptureForm`, exactly like the key above.
    slot.dataset.coverage = card._afCoverage || '';
    slot.dataset.stocks = card._afStocks || '';
    window.AirfluxCapture?.mount(slot, '', card._afDated || card._afNamed || 'my car', 'coverage_gap');
    btn.hidden = true;
  });

  // From here on the customer's own last word wins over the address bar.
  document.addEventListener('airflux:car-changed', () => {
    honourUrl = false;
    repaint();
  });

  window.AirfluxCards = {
    repaint,
    /** The most recent announcement, for anything that loaded after it. */
    get last() {
      return last;
    },
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', repaint);
  else repaint();
})();
